import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import CountUp from 'react-countup';
import { usersAPI, artistsAPI } from '../services/api';
import VideoCard from '../components/VideoCard';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001/api/v1';

// ────────────────────────────────────────────────────────────────
// SVG Social Icons
// ────────────────────────────────────────────────────────────────

const InstagramIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
  </svg>
);

const TwitterIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.747l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const YouTubeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

const TikTokIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.22 8.22 0 004.84 1.56V6.79a4.85 4.85 0 01-1.07-.1z"/>
  </svg>
);

const SOCIAL_ICONS = {
  instagram: InstagramIcon,
  twitter: TwitterIcon,
  youtube: YouTubeIcon,
  tiktok: TikTokIcon,
};

const SOCIAL_LABELS = {
  instagram: 'Instagram',
  twitter: 'X / Twitter',
  youtube: 'YouTube',
  tiktok: 'TikTok',
};

// ────────────────────────────────────────────────────────────────
// Streak helpers
// ────────────────────────────────────────────────────────────────

const formatStreak = (streak) => {
  if (!streak || streak === 0) return '-';
  return streak > 0 ? `W${streak}` : `L${Math.abs(streak)}`;
};

const streakColorClass = (streak) => {
  if (!streak || streak === 0) return 'text-gray-500';
  return streak > 0 ? 'text-green-400' : 'text-red-400';
};

// ────────────────────────────────────────────────────────────────
// Animation variants
// ────────────────────────────────────────────────────────────────

const heroVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.19, 1, 0.22, 1] },
  },
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.08, delayChildren: 0.15 },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.19, 1, 0.22, 1] },
  },
};

const avatarVariants = {
  hidden: { opacity: 0, scale: 0.85 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.55, ease: [0.19, 1, 0.22, 1] },
  },
};

const cardStagger = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.06, delayChildren: 0.1 },
  },
};

const cardFadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: [0.19, 1, 0.22, 1] },
  },
};

// ────────────────────────────────────────────────────────────────
// Animated stat pill with count-up visual layer
//
// Test-safety: the static value string is always in the DOM (inside
// a visually-hidden span that holds the data-testid). CountUp renders
// in an aria-hidden sibling so tests always find the correct final
// value without waiting for animation. Disabled entirely when the
// user prefers reduced motion.
// ────────────────────────────────────────────────────────────────

const StatPill = ({ label, value, accent, colorClass, testId, countTarget, decimals }) => {
  const reduced = useReducedMotion();
  const showCountUp = countTarget !== undefined && !reduced;

  return (
    <motion.div
      data-testid={testId}
      variants={fadeUp}
      className="flex flex-col items-center px-5 py-4 rounded-xl border border-army/40 bg-battle-dark/60 backdrop-blur-sm min-w-[72px] relative"
      whileHover={reduced ? {} : { scale: 1.05, borderColor: 'rgba(245,230,66,0.35)' }}
      transition={{ duration: 0.2 }}
    >
      <span className="text-gray-500 text-[10px] font-bold uppercase tracking-widest mb-1">{label}</span>

      {/* Wrapper keeps both layers in the same space; static wins for layout sizing */}
      <span className="relative flex items-center justify-center">
        {/* Static value: always in DOM for test assertions and accessibility.
            Invisible while CountUp plays so layout is driven by CountUp overlay. */}
        <span
          className={`text-xl md:text-2xl font-black ${colorClass || (accent ? 'text-brand-yellow' : 'text-white')} ${showCountUp ? 'opacity-0 select-none pointer-events-none' : ''}`}
        >
          {value}
        </span>

        {/* Count-up visual overlay: aria-hidden so screen readers read the static value */}
        {showCountUp && (
          <span
            aria-hidden="true"
            className={`text-xl md:text-2xl font-black absolute inset-0 flex items-center justify-center ${colorClass || (accent ? 'text-brand-yellow' : 'text-white')}`}
          >
            <CountUp
              start={0}
              end={countTarget}
              duration={1.4}
              decimals={decimals || 0}
              suffix={decimals ? '%' : undefined}
              useEasing
            />
          </span>
        )}
      </span>
    </motion.div>
  );
};

// ────────────────────────────────────────────────────────────────
// Hero stat: the big three (rank / record / elo)
// ────────────────────────────────────────────────────────────────

const HeroStat = ({ label, value, countTarget, decimals, accent, colorClass, testId }) => {
  const reduced = useReducedMotion();
  const showCountUp = countTarget !== undefined && !reduced;
  const numberClass = `font-display font-black text-4xl md:text-5xl leading-none ${colorClass || (accent ? 'text-brand-yellow' : 'text-white')}`;

  return (
    <motion.div
      data-testid={testId}
      variants={fadeUp}
      className="flex flex-col items-center px-3"
      whileHover={reduced ? {} : { scale: 1.04 }}
      transition={{ duration: 0.2 }}
    >
      <span className="text-gray-500 text-[11px] font-bold uppercase tracking-widest mb-2">{label}</span>
      <span className="relative flex items-center justify-center">
        <span className={`${numberClass} ${showCountUp ? 'opacity-0 select-none pointer-events-none' : ''}`}>{value}</span>
        {showCountUp && (
          <span aria-hidden="true" className={`${numberClass} absolute inset-0 flex items-center justify-center`}>
            <CountUp start={0} end={countTarget} duration={1.6} decimals={decimals || 0} useEasing />
          </span>
        )}
      </span>
    </motion.div>
  );
};

// ────────────────────────────────────────────────────────────────
// Pulsing avatar ring (artist only)
// ────────────────────────────────────────────────────────────────

const AvatarRing = ({ children, isArtist, reduced }) => {
  if (!isArtist) return <div className="relative mb-5">{children}</div>;

  return (
    <div className="relative mb-5">
      {/* Outer spotlight radial glow */}
      <motion.div
        className="absolute -inset-8 rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(245,230,66,0.12) 0%, rgba(74,93,58,0.08) 50%, transparent 70%)',
        }}
        animate={reduced ? {} : {
          opacity: [0.6, 1, 0.6],
          scale: [0.95, 1.05, 0.95],
        }}
        transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
      />
      {/* Yellow ring pulse */}
      <motion.div
        className="absolute -inset-1 rounded-full pointer-events-none"
        style={{
          background: 'conic-gradient(from 0deg, rgba(245,230,66,0.35), rgba(74,93,58,0.25), rgba(245,230,66,0.35))',
          filter: 'blur(2px)',
        }}
        animate={reduced ? {} : { rotate: 360 }}
        transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
      />
      <div className="absolute -inset-1 rounded-full bg-gradient-to-br from-brand-yellow/20 to-army/15 blur-sm" />
      {children}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────
// Rank badge with shimmer gleam
// ────────────────────────────────────────────────────────────────

const RankedBadge = ({ rank, reduced }) => (
  <motion.span
    variants={fadeUp}
    className="relative inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-yellow/10 border border-brand-yellow/30 text-brand-yellow text-xs font-bold uppercase tracking-wider mb-4 overflow-hidden"
    data-testid="artist-badge"
  >
    {!reduced && (
      <motion.span
        className="absolute inset-0 -skew-x-12 pointer-events-none"
        style={{
          background: 'linear-gradient(90deg, transparent 0%, rgba(245,230,66,0.35) 50%, transparent 100%)',
          width: '40%',
        }}
        animate={{ x: ['-120%', '220%'] }}
        transition={{ duration: 2.2, repeat: Infinity, repeatDelay: 3, ease: 'easeInOut' }}
      />
    )}
    <svg className="w-3.5 h-3.5 relative" fill="currentColor" viewBox="0 0 20 20">
      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
    </svg>
    <span className="relative">#{rank} Ranked</span>
  </motion.span>
);

// ────────────────────────────────────────────────────────────────
// Skeleton while loading
// ────────────────────────────────────────────────────────────────

const ProfileSkeleton = () => (
  <div className="min-h-screen py-8 px-4">
    <div className="max-w-3xl mx-auto">
      <div className="flex flex-col items-center gap-4 mb-10">
        <div className="w-28 h-28 rounded-full bg-battle-gray relative overflow-hidden">
          <div className="skeleton-shimmer absolute inset-0" />
        </div>
        <div className="h-7 w-44 rounded bg-battle-gray relative overflow-hidden">
          <div className="skeleton-shimmer absolute inset-0" />
        </div>
        <div className="h-4 w-28 rounded bg-battle-gray relative overflow-hidden">
          <div className="skeleton-shimmer absolute inset-0" />
        </div>
      </div>
    </div>
  </div>
);

// ────────────────────────────────────────────────────────────────
// Animated video card wrapper
// ────────────────────────────────────────────────────────────────

const AnimatedVideoCard = ({ video, reduced }) => (
  <motion.div
    variants={cardFadeUp}
    whileHover={reduced ? {} : { y: -4, scale: 1.02 }}
    transition={{ duration: 0.2 }}
  >
    <VideoCard video={video} />
  </motion.div>
);

// ────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────

const UserProfile = () => {
  const { id } = useParams();
  const [profile, setProfile] = useState(null);
  const [artistVideos, setArtistVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const reduced = useReducedMotion();

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await usersAPI.getPublicProfile(id);
        const data = response.data;
        setProfile(data);

        if (data.is_artist) {
          try {
            const artistRes = await artistsAPI.getById(id);
            if (artistRes.data && artistRes.data.videos) {
              setArtistVideos(artistRes.data.videos);
            }
          } catch {
            // Videos are a best-effort enhancement; not fatal.
          }
        }
      } catch (error) {
        if (error.response && error.response.status === 404) {
          setNotFound(true);
        } else {
          console.error('Error fetching profile:', error);
          setNotFound(true);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="loading-indicator">
        <ProfileSkeleton />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="not-found">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">404</h1>
          <p className="text-gray-400 mb-6">User not found</p>
          <Link to="/" className="text-army hover:underline">Go home</Link>
        </div>
      </div>
    );
  }

  // Avatar resolution priority:
  // 1. Uploaded avatar (has_avatar -> GET /api/v1/users/:id/avatar)
  // 2. Google profile picture (profile.avatar_url)
  // 3. Initial-letter placeholder
  const uploadedAvatarSrc = profile.has_avatar ? `${API_URL}/users/${id}/avatar` : null;
  const googleAvatarSrc = !profile.has_avatar && profile.avatar_url ? profile.avatar_url : null;
  const avatarUrl = uploadedAvatarSrc || googleAvatarSrc || null;

  const socialLinks = profile.social_links || {};
  const hasSocialLinks = Object.values(socialLinks).some(v => v);
  const isArtist = profile.is_artist;
  const stats = isArtist ? profile.artist_stats : null;

  const initials = (profile.display_name || profile.username || '?').charAt(0).toUpperCase();

  return (
    <div className="min-h-screen" data-testid="user-profile">

      {/* ── Hero Section ──────────────────────────────────────────── */}
      <section className="relative overflow-hidden pt-12 pb-8 px-4">
        {/* Cinematic spotlight radial behind content */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(74,93,58,0.12)_0%,transparent_60%)] pointer-events-none" />
        {isArtist && (
          <motion.div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'radial-gradient(ellipse 60% 40% at 50% 0%, rgba(245,230,66,0.07) 0%, transparent 70%)',
            }}
            animate={reduced ? {} : { opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          />
        )}

        <motion.div
          className="relative max-w-3xl mx-auto flex flex-col items-center text-center"
          variants={staggerContainer}
          initial={reduced ? 'visible' : 'hidden'}
          animate="visible"
        >

          {/* Avatar */}
          <motion.div variants={avatarVariants}>
            <AvatarRing isArtist={isArtist} reduced={reduced}>
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={profile.display_name}
                  className={`relative w-28 h-28 md:w-36 md:h-36 rounded-full object-cover border-4 ${isArtist ? 'border-army/50' : 'border-army/30'}`}
                  data-testid="profile-avatar"
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                    if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div
                className={`relative w-28 h-28 md:w-36 md:h-36 rounded-full flex items-center justify-center font-black text-5xl md:text-6xl border-4 ${isArtist ? 'bg-army/20 border-army/50 text-brand-yellow' : 'bg-army/10 border-army/30 text-white'}`}
                data-testid="avatar-placeholder"
                style={{ display: avatarUrl ? 'none' : 'flex' }}
              >
                {initials}
              </div>
            </AvatarRing>
          </motion.div>

          {/* Name + handle */}
          <motion.h1
            variants={fadeUp}
            className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight text-white mb-1"
            data-testid="display-name"
          >
            {profile.display_name}
          </motion.h1>
          <motion.p variants={fadeUp} className="text-gray-400 text-sm mb-2" data-testid="username">
            @{profile.username}
          </motion.p>

          {/* Artist badge */}
          {isArtist && stats && stats.rank && (
            <RankedBadge rank={stats.rank} reduced={reduced} />
          )}
          {isArtist && (!stats || !stats.rank) && (
            <motion.span
              variants={fadeUp}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-army/20 border border-army/30 text-army-light text-xs font-bold uppercase tracking-wider mb-4"
              data-testid="artist-badge"
            >
              Rapper
            </motion.span>
          )}

          {/* Member since */}
          <motion.p variants={fadeUp} className="text-gray-600 text-xs mb-6" data-testid="member-since">
            Member since {profile.member_since}
          </motion.p>

          {/* Bio */}
          {profile.bio && (
            <motion.div variants={fadeUp} className="max-w-xl mx-auto mb-6">
              <p
                className="text-gray-300 text-sm md:text-base leading-relaxed whitespace-pre-wrap"
                data-testid="bio"
              >
                {profile.bio}
              </p>
            </motion.div>
          )}

          {/* Social links */}
          {hasSocialLinks && (
            <motion.div
              variants={fadeUp}
              className="flex justify-center gap-5 mb-6 flex-wrap"
              data-testid="social-links"
            >
              {Object.entries(socialLinks).map(([platform, url]) => {
                if (!url) return null;
                const Icon = SOCIAL_ICONS[platform];
                if (!Icon) return null;
                return (
                  <motion.a
                    key={platform}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    title={SOCIAL_LABELS[platform] || platform}
                    className="text-gray-500 hover:text-brand-yellow transition-colors duration-200"
                    data-testid={`social-link-${platform}`}
                    whileHover={reduced ? {} : { scale: 1.25 }}
                    transition={{ duration: 0.18 }}
                  >
                    <Icon />
                  </motion.a>
                );
              })}
            </motion.div>
          )}

          {/* Comments count (viewer) */}
          {!isArtist && (
            <motion.span variants={fadeUp} className="text-gray-600 text-xs" data-testid="comments-count">
              {profile.comments_count} {profile.comments_count === 1 ? 'comment' : 'comments'}
            </motion.span>
          )}
        </motion.div>
      </section>

      {/* ── Rapper Stats Band (artists only) ──────────────────────── */}
      {isArtist && stats && (
        <motion.section
          className="max-w-3xl mx-auto px-4 mb-10"
          data-testid="artist-stats-band"
          variants={heroVariants}
          initial={reduced ? 'visible' : 'hidden'}
          animate="visible"
        >
          {/* Divider bar */}
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px flex-1 bg-army/30" />
            <span className="text-gray-600 text-[10px] font-bold uppercase tracking-widest">Battle Record</span>
            <div className="h-px flex-1 bg-army/30" />
          </div>

          {/* Hero trio: rank / record / elo, big and bold */}
          <motion.div
            className="flex flex-wrap items-end justify-center gap-8 md:gap-14 mb-8"
            variants={staggerContainer}
            initial={reduced ? 'visible' : 'hidden'}
            animate="visible"
          >
            {stats.rank && (
              <HeroStat testId="stat-rank" label="Rank" value={`#${stats.rank}`} accent />
            )}
            <HeroStat
              testId="stat-record"
              label="Record"
              value={`${stats.wins}-${stats.losses}`}
            />
            <HeroStat
              testId="stat-elo"
              label="ELO"
              value={stats.elo_rating}
              countTarget={stats.elo_rating}
              accent
            />
          </motion.div>

          {/* Secondary stats */}
          <motion.div
            className="flex flex-wrap justify-center gap-3"
            variants={staggerContainer}
            initial={reduced ? 'visible' : 'hidden'}
            animate="visible"
          >
            <StatPill
              testId="stat-winrate"
              label="Win Rate"
              value={`${stats.win_percentage}%`}
              countTarget={stats.win_percentage}
              decimals={1}
            />
            <StatPill
              testId="stat-streak"
              label="Streak"
              value={formatStreak(stats.current_streak)}
              colorClass={streakColorClass(stats.current_streak)}
            />
            <StatPill
              testId="stat-battles"
              label="Battles"
              value={stats.total_battles}
              countTarget={stats.total_battles}
            />
          </motion.div>

          {/* Comments count for artists, below stats */}
          <p className="text-center text-gray-600 text-xs mt-4" data-testid="comments-count">
            {profile.comments_count} {profile.comments_count === 1 ? 'comment' : 'comments'}
          </p>
        </motion.section>
      )}

      {/* ── Battle Videos (artists only, best-effort) ─────────────── */}
      {isArtist && artistVideos.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 pb-16" data-testid="artist-battles">
          <div className="row-section-header mb-6">
            <h2 className="section-title text-white text-2xl">
              BATTLES
            </h2>
          </div>
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            variants={cardStagger}
            initial={reduced ? 'visible' : 'hidden'}
            animate="visible"
          >
            {artistVideos.map((video) => (
              <AnimatedVideoCard key={video.id} video={video} reduced={reduced} />
            ))}
          </motion.div>
        </section>
      )}

    </div>
  );
};

export default UserProfile;
